import {
  useQuery,
  useSubscription,
  useSuspenseQuery,
} from '@apollo/client/react';
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
  Button,
  Group,
  Input,
} from '@chakra-ui/react';
import { Suspense, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { GET_DOCUMENT_BY_PATH } from '#/graphql/queries/document';
import { DOCUMENT_PICKER_LIST } from '#/graphql/queries/document-picker-list';
import { DOCUMENT_UPDATED } from '#/graphql/subscriptions/document-updated';
import { useAppConfig } from '#/providers/app-config-provider';
import { useAppSettings } from '#/providers/app-settings-provider';
import { useCurrentUser } from '#/providers/user-provider';
import {
  isValidDocumentPath,
  DOCUMENT_PATH_DESCRIPTION,
} from '#/utils/document-path';
import { formatDocumentName } from '#/utils/format-document-name';

import { DocumentFilesPage } from './files/document-files-page';
import { DocumentHistory } from './history/document-history';
import { ViewDocument } from './view';

const DOCUMENTS_FILES_SUFFIX = '/files';

function parseDocumentsRoute(pathname: string): {
  documentPath?: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
  filesRedirectTo?: string;
  compareRedirectTo?: string;
} {
  const pathMatch = pathname.match(/^\/documents\/(.+)$/);
  if (!pathMatch) {
    return {
      isHistoryMode: false,
      isFilesPage: false,
    };
  }
  const fullPath = pathMatch[1];

  if (
    fullPath.endsWith(DOCUMENTS_FILES_SUFFIX) &&
    fullPath.length > DOCUMENTS_FILES_SUFFIX.length
  ) {
    const afterStrip = fullPath.slice(0, -DOCUMENTS_FILES_SUFFIX.length);
    const mixedHistory =
      afterStrip.endsWith('/history') ||
      afterStrip.includes('/history/compare/');
    if (mixedHistory) {
      let basePath: string | undefined;
      if (afterStrip.includes('/history/compare/')) {
        const compareMatch = afterStrip.match(/^(.+)\/history\/compare\/.+/);
        basePath = compareMatch ? compareMatch[1] : undefined;
      } else if (afterStrip.endsWith('/history')) {
        basePath = afterStrip.slice(0, -'/history'.length);
      }
      if (basePath) {
        return {
          documentPath: basePath,
          isHistoryMode: false,
          isFilesPage: true,
          filesRedirectTo: `/documents/${basePath}${DOCUMENTS_FILES_SUFFIX}`,
        };
      }
    }
    return {
      documentPath: afterStrip,
      isHistoryMode: false,
      isFilesPage: true,
    };
  }

  /** Legacy compare URLs now redirect to document history (diff lives in expanded rows). */
  if (fullPath.includes('/history/compare/')) {
    const compareMatch = fullPath.match(/^(.+)\/history\/compare\/.+/);
    const documentPath = compareMatch ? compareMatch[1] : undefined;
    if (documentPath) {
      return {
        documentPath,
        isHistoryMode: false,
        isFilesPage: false,
        compareRedirectTo: `/documents/${documentPath}/history`,
      };
    }
  }

  const isHistoryMode = fullPath.endsWith('/history');
  let documentPath: string | undefined;
  if (isHistoryMode) {
    documentPath = fullPath.slice(0, -'/history'.length);
  } else {
    documentPath = fullPath || undefined;
  }
  return {
    documentPath,
    isHistoryMode,
    isFilesPage: false,
  };
}

/** Single document query; passes document + refetch to view/history. Defined at module level so React keeps a stable component identity and avoids re-mount loops. */
function DocumentPageWithData({
  documentPath,
  isHistoryMode,
  isFilesPage,
}: {
  documentPath: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
}) {
  const { config } = useAppConfig();
  const user = useCurrentUser();
  const { data, refetch: refetchDocument } = useSuspenseQuery(
    GET_DOCUMENT_BY_PATH,
    {
      variables: { path: documentPath },
    },
  );
  const result = data?.documentByPath;
  const subscribedDocumentId =
    result?.__typename === 'Document' ? result.id : undefined;

  const lastServerEditTimestampRef = useRef(0);

  useSubscription(DOCUMENT_UPDATED, {
    variables: { documentId: subscribedDocumentId ?? '' },
    skip: !subscribedDocumentId,
    onData: ({ data: subData }) => {
      const ts = subData.data?.documentUpdated?.editTimestamp;
      if (typeof ts !== 'number') return;
      if (ts <= lastServerEditTimestampRef.current) return;
      lastServerEditTimestampRef.current = ts;
      void refetchDocument();
    },
  });

  const path =
    result?.__typename === 'Document'
      ? result.path
      : result?.__typename === 'DocumentNotFound'
        ? (result.requestedPath ?? documentPath)
        : documentPath;

  if (isFilesPage) {
    return (
      <DocumentFilesPage
        documentResult={result}
        requestedPath={documentPath}
        apiHost={config.apiHost}
        userEmail={user.email}
      />
    );
  }
  if (isHistoryMode) {
    return (
      <DocumentHistory
        document={result?.__typename === 'Document' ? result : null}
        path={path}
        refetch={refetchDocument}
      />
    );
  }
  return (
    <ViewDocument
      key={path}
      document={result}
      path={path}
      refetch={refetchDocument}
      lastServerEditTimestampRef={lastServerEditTimestampRef}
    />
  );
}

export function DocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [createPath, setCreatePath] = useState('');
  const { documentPathRegex } = useAppSettings();

  const route = useMemo(
    () => parseDocumentsRoute(location.pathname),
    [location.pathname],
  );

  useLayoutEffect(() => {
    if (route.filesRedirectTo) {
      navigate(route.filesRedirectTo, { replace: true });
    }
  }, [navigate, route.filesRedirectTo]);

  useLayoutEffect(() => {
    if (route.compareRedirectTo) {
      navigate(route.compareRedirectTo, { replace: true });
    }
  }, [navigate, route.compareRedirectTo]);

  const routeRedirectPending = Boolean(
    route.filesRedirectTo || route.compareRedirectTo,
  );
  const documentPath = routeRedirectPending ? undefined : route.documentPath;
  const { isHistoryMode, isFilesPage } = route;

  const trimmedCreatePath = createPath.trim();
  const isCreatePathValid = trimmedCreatePath
    ? isValidDocumentPath(trimmedCreatePath, documentPathRegex)
    : false;

  const handleCreateSubmit = () => {
    const path = createPath.trim();
    if (!path || !isValidDocumentPath(path, documentPathRegex)) return;
    setCreatePath('');
    navigate(`/documents/${path}`);
  };

  const { data: documentsData, loading: documentsLoading } = useQuery(
    DOCUMENT_PICKER_LIST,
    {
      skip: !!documentPath,
      fetchPolicy: 'cache-and-network',
    },
  );
  const documents = documentsData?.documents ?? [];

  return (
    <>
      {routeRedirectPending ? (
        <Box
          flex={1}
          alignSelf="stretch"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH={0}
          width="100%"
        >
          <Spinner size="xl" />
        </Box>
      ) : documentPath ? (
        <Suspense
          fallback={
            <Box
              flex={1}
              alignSelf="stretch"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minH={0}
              width="100%"
            >
              <Spinner size="xl" />
            </Box>
          }
        >
          <DocumentPageWithData
            documentPath={documentPath}
            isHistoryMode={isHistoryMode}
            isFilesPage={isFilesPage}
          />
        </Suspense>
      ) : (
        <Box width="100%" p={4}>
          <Box maxW="2xl" mx="auto" width="100%">
            <VStack gap={8} width="100%" align="stretch">
              <VStack gap={4} width="100%" align="stretch">
                <Text fontSize="lg" color="fg" fontWeight="medium">
                  Create document
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  Enter a path to create a new document. You'll be taken to the
                  document page where you can create it.
                </Text>
                <VStack gap={2} align="stretch">
                  <Group
                    attached
                    width="100%"
                    minW={0}
                    display="grid"
                    gridTemplateColumns="1fr auto"
                    alignItems="stretch"
                  >
                    <Input
                      placeholder="e.g. folder/doc-name or path (with parens)"
                      value={createPath}
                      onChange={(e) => setCreatePath(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateSubmit();
                      }}
                      aria-invalid={!!trimmedCreatePath && !isCreatePathValid}
                      borderColor={
                        trimmedCreatePath && !isCreatePathValid
                          ? 'red.500'
                          : undefined
                      }
                      minW={0}
                    />
                    <Button
                      colorScheme="blue"
                      onClick={handleCreateSubmit}
                      disabled={!trimmedCreatePath || !isCreatePathValid}
                      style={{
                        height: '100%',
                        minHeight: '100%',
                        alignSelf: 'stretch',
                      }}
                    >
                      Create
                    </Button>
                  </Group>
                  {trimmedCreatePath && !isCreatePathValid && (
                    <Text fontSize="xs" color="red.500">
                      {DOCUMENT_PATH_DESCRIPTION}
                    </Text>
                  )}
                </VStack>
              </VStack>
              <VStack gap={2} align="stretch" width="100%">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="fg.muted"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Your documents
                </Text>
                {documentsLoading ? (
                  <HStack py={2} gap={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="fg.muted">
                      Loading…
                    </Text>
                  </HStack>
                ) : documents.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    No documents yet.
                  </Text>
                ) : (
                  <>
                    {documents.map((doc) => {
                      const docHref = `/documents/${doc.path}`;
                      const label = formatDocumentName(doc.path).join(' > ');
                      return (
                        <Link
                          key={doc.id}
                          to={docHref}
                          style={{ textDecoration: 'none' }}
                        >
                          <HStack
                            gap={3}
                            py={2}
                            px={1}
                            borderRadius="md"
                            _hover={{ bg: 'bg.muted' }}
                            _active={{ bg: 'bg.emphasized' }}
                          >
                            <Text
                              fontWeight="normal"
                              color="fg"
                              fontSize="sm"
                              lineClamp={1}
                            >
                              {label}
                            </Text>
                          </HStack>
                        </Link>
                      );
                    })}
                  </>
                )}
              </VStack>
            </VStack>
          </Box>
        </Box>
      )}
    </>
  );
}
